// Mock data for The Chimera Gallery
// Each artwork supports two images:
//   - image:        the close-up / catalog view of the artwork
//   - imageContext: the styled gallery-wall / contextual view

// Helper: convert artist name to URL slug (e.g. "Kate Beagan" -> "kate-beagan")
export const slugify = (name) =>
  name.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');

// Helper: parse a size string like "30 × 35 cm" or "100x80 cm" -> { width, height } in cm
export const parseSize = (sizeStr) => {
  if (!sizeStr) return null;
  const match = sizeStr.match(/(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/i);
  if (!match) return null;
  return { width: parseFloat(match[1]), height: parseFloat(match[2]) };
};

// Display-size mapping for paintings on cards.
// The box uses the painting's REAL aspect ratio (so landscape canvases render
// landscape, portrait render portrait), scaled by sqrt(area) for natural
// "feels right" relative sizing. Range ~60% (small studies) to ~92% (largest).
export const getDisplaySize = (sizeStr) => {
  const size = parseSize(sizeStr);
  if (!size) return { width: '85%', height: '85%' };
  const area = size.width * size.height;
  const MAX_AREA = 8000; // 100 × 80 reference (the biggest piece in stock)
  const normalized = Math.min(1, Math.sqrt(area / MAX_AREA));
  const basePct = 60 + normalized * 32; // 60 → 92
  const longest = Math.max(size.width, size.height);
  return {
    width: `${basePct * (size.width / longest)}%`,
    height: `${basePct * (size.height / longest)}%`,
  };
};

export const galleryItems = [
  {
    id: 1,
    title: "Kitchen Colour",
    artist: "Mark Bailey",
    year: 2025,
    size: "30 × 35 cm",
    price: "€1,200",
    image: "/bailey-kitchen-catalogue-0.png",
    imageContext: "/bailey-kitchen-context-0.png",
    category: "Painting",
    featured: true,
    description: "A loose painterly study of a chef at work — the figure emerging from an abstract scaffolding of greens, peaches and cool greys. Oil on canvas."
  },
  {
    id: 18,
    title: "Mixer",
    artist: "Mark Bailey",
    year: 2020,
    size: "50 × 50 cm",
    price: "€2,500",
    image: "/bailey-mixer-catalogue-0.png",
    imageContext: "/bailey-mixer-context-0.png",
    category: "Painting",
    description: "A behind-the-bar study — a bartender in deep blue intent over a coupe glass, framed by a shimmering wall of bottles and a single bloom of marigold. Oil on canvas."
  },
  {
    id: 19,
    title: "L'Infirmière et le Soldat Blessé",
    artist: "H. Trémo…",
    year: "c. 1915",
    size: "60 × 30 cm",
    price: "€900",
    image: "/spelter-nurse-catalogue-0.png",
    imageContext: "/spelter-nurse-context-0.png",
    category: "Sculpture",
    featured: true,
    description: "French WWI patriotic spelter group depicting a Red Cross nurse hailing for help beside a wounded French infantryman in his Adrian helmet. Mounted on a turned red marble socle, with original orange enamel highlight surviving on the Red Cross armband. Signed 'H. Trémo…' to the base. Circa 1915–1920."
  },
  {
    id: 20,
    title: "Schlumberger Alsace Champagne Bucket",
    artist: "Domaines Schlumberger, Alsace",
    year: "c. 1960",
    size: "H 33 cm",
    price: "€240",
    image: "/schlumberger-bucket-catalogue-0.png",
    imageContext: "/schlumberger-bucket-context-0.png",
    category: "Antique",
    featured: true,
    description: "French coopered oak champagne bucket from Domaines Schlumberger, Guebwiller. Three copper bands and embossed copper escutcheon with the Schlumberger coat of arms. Twin raised stave 'ear' handles, warm honey patina. Mid-20th century."
  },
  {
    id: 21,
    title: "Generational",
    artist: "Dirk Hudson",
    year: 2024,
    size: "50 × 34 × 12 cm",
    price: "€2,000",
    image: "/hudson-gene-catalogue-0.png",
    imageContext: "/hudson-gene-context-0.png",
    category: "Sculpture",
    featured: true,
    description: "Two standing sentinels in textured blackened steel rise from a steel base, copper veins creeping upward like molten roots. The taller figure carries a single copper face — a meditation on lineage, presence and ancestry. Steel, copper and beeswax."
  },
  {
    id: 22,
    title: "Bushfire",
    artist: "Dirk Hudson",
    year: 2024,
    size: "35 × 26 × 17 cm",
    price: "€775",
    image: "/hudson-bushfire-catalogue-0.png",
    imageContext: null,
    category: "Sculpture",
    featured: false,
    description: "A fan of slender copper leaves rises from a single hammered stem, the patina ranging from sunset orange through deep umber to flashes of green-blue oxidation. Set on a textured copper base. Solid copper."
  },
  {
    id: 23,
    title: "Art Deco French Liqueur Set",
    artist: "Verrerie d'Arques, France",
    year: "1930s",
    size: "Complete set: decanter, tray + 6 glasses",
    price: "€350",
    image: "/arques-liqueur-catalogue-0.png",
    imageContext: "/arques-liqueur-context-0.png",
    category: "Antique",
    featured: true,
    description: "French Art Deco liqueur set in pink 'rosaline' pressed glass — decanter with stopper, octagonal tray with geometric chevron motif, and six matching glasses. From the Verrerie et Cristallerie d'Arques (G. Durand & Cie), c. 1930s. A characteristic example of the golden age of European Art Deco glassmaking."
  },
  {
    id: 24,
    title: "The Time Between Us",
    artist: "Eamonn Higgins",
    year: 2024,
    size: "35 × 20 × 15 cm",
    price: "€2,500",
    image: "/higgins-time-catalogue-0.png",
    imageContext: "/higgins-time-context-0.png",
    category: "Sculpture",
    featured: true,
    description: "A portrait bust in patinated bronze of a weathered, lined face — quiet, watchful, carrying the weight of years. Mounted on a polished dark marble plinth. A meditation on memory, presence and the distance time places between us."
  },
  {
    id: 25,
    title: "Patek Philippe Limoges Bonbonnière",
    artist: "Patek Philippe / Limoges, France",
    year: "Collection 2000",
    size: "10 × 4 × 3.5 cm",
    price: "€650",
    image: "/patek-bonbonniere-catalogue-0.png",
    imageContext: "/patek-bonbonniere-context-0.png",
    category: "Antique",
    featured: true,
    description: "A fine and rare hand-decorated Limoges porcelain bonbonnière from the Patek Philippe Collection 2000 limited edition — reproducing the cobalt-and-gold enamelled case of an iconic 1928 Patek Philippe pocket watch held in the Patek Philippe Museum, Geneva. Octagonal porcelain de Limoges, signed 'Patek Philippe Genève'. Excellent condition."
  },
  {
    id: 26,
    title: "Bay Keramik 'Fat Lava' Vase",
    artist: "Bay-Keramik, West Germany",
    year: "c. 1960s–70s",
    size: "H 40 cm",
    price: "€195",
    image: "/bay-fatlava-catalogue-0.png",
    imageContext: "/bay-fatlava-context-0.png",
    category: "Antique",
    featured: true,
    description: "Substantial West German 'Fat Lava' (Fettlava) vase by Bay-Keramik-Fabrik Bodo Mans, Ransbach-Baumbach. Cream stoneware with a dripping amber-caramel glaze band and characteristic wavy scalloped flow line. Impressed marks: 'BAY · W-GERMANY · 240 40' — form 240, height 40 cm. A bold, large-format example of the West German Pottery movement."
  },
  {
    id: 27,
    title: "Petrol-Teal & Gilt Upcycled Miniature Armoire",
    artist: "Upcycled vintage cabinet, France",
    year: "Upcycled vintage",
    size: "30 × 20 × 13 cm",
    price: "€120",
    image: "/jbox-catalogue-0.png",
    imageContext: "/jbox-context-0.png",
    category: "Antique",
    featured: true,
    description: "A charming miniature Florentine-style armoire / jewellery cabinet, lovingly upcycled in deep petrol-teal chalk paint with rubbed-back warm gold gilt edges and feet. Two arched glass doors with etched cherry-blossom and grass motifs, two small drawers below dressed in a delicate lace-pattern stencil, shaped scrolled bracket feet."
  },
  {
    id: 2,
    title: "Emerald Glade",
    artist: "Kyle Barnes",
    year: 2025,
    size: "100 × 80 cm",
    price: "€9,750",
    image: "/barnes-emerald-catalogue-0.png",
    imageContext: "/barnes-emerald-context-0.png",
    category: "Painting",
    featured: true,
    description: "A bold hyper-real portrait — a metallic patinated head set against a vivid coral ground. A striking contemporary statement piece. Oil on canvas."
  },
  {
    id: 3,
    title: "At the Top",
    artist: "Igor Melnyk",
    year: 2025,
    size: "20 × 25 cm",
    price: "€275",
    image: "/melnyk-top-catalogue-0.png",
    imageContext: "/melnyk-top-context-0.png",
    category: "Painting",
    description: "A vivid impasto study — a dark cottage with terracotta roof glimpsed beyond pale birches, in cool teals, ochres and emerald gestural strokes. Oil on board."
  },
  {
    id: 4,
    title: "Summer is Sleeping",
    artist: "Igor Melnyk",
    year: 2025,
    size: "25 × 20 cm",
    price: "€275",
    image: "/melnyk-summer-catalogue-0.png",
    imageContext: "/melnyk-summer-context-0.png",
    category: "Painting",
    featured: true,
    description: "A vivid expressionist landscape in heavy impasto — autumn trees against a quiet mountain backdrop, painted with confident gestural strokes. Oil on board."
  },
  {
    id: 15,
    title: "Submerged",
    artist: "Lesley Oldaker",
    year: 2025,
    size: "61 × 46 cm",
    price: "€1,500",
    image: "/oldaker-submerged-catalogue-0.png",
    imageContext: "/oldaker-submerged-context-0.png",
    category: "Painting",
    featured: true,
    description: "A vast cobalt archway recedes into infinity as nine cloaked figures stand watching a single distant red form. Oil on canvas — quietly cinematic, charged with stillness and threat."
  },
  {
    id: 16,
    title: "Pause",
    artist: "Lesley Oldaker",
    year: 2025,
    size: "91 × 61 cm",
    price: "€2,250",
    image: "/oldaker-pause-catalogue-0.png",
    imageContext: "/oldaker-pause-context-0.png",
    category: "Painting",
    description: "A long glass-roofed concourse fills with anonymous figures moving in silence — and one stranger in red paused at the railing. A meditation on stillness inside the crowd. Oil on canvas."
  },
  {
    id: 17,
    title: "A Change of Course",
    artist: "Lesley Oldaker",
    year: 2025,
    size: "91 × 61 cm",
    price: "€2,250",
    image: "/oldaker-change-catalogue-0.png",
    imageContext: "/oldaker-change-context-0.png",
    category: "Painting",
    description: "A converging crowd dissolves downward in trailing rivulets of indigo and black, set against a single horizon of crimson and ember. A study of collective momentum and quiet dissent. Oil on canvas."
  },
  {
    id: 5,
    title: "Piazza Della Signoria",
    artist: "Brian Smyth",
    year: 2016,
    size: "70 × 50 cm",
    price: "€1,700",
    image: "/smyth-piazza-catalogue-0.png",
    imageContext: "/smyth-piazza-context-0.png",
    category: "Painting",
    featured: true,
    description: "A luminous aerial view of Florence's Piazza della Signoria — figures drifting across the sunlit square beneath the bronze equestrian statue and the warm canopies of the market. Oil on canvas."
  },
  {
    id: 6,
    title: "Evening Drive",
    artist: "Kate Beagan",
    year: 2024,
    size: "100 × 80 cm",
    price: "€3,000",
    image: "https://customer-assets.emergentagent.com/job_deco-treasures/artifacts/rll4npw2_IMG_6743.jpg",
    imageContext: "/beagan-evening-context-0.png",
    category: "Painting",
    featured: true,
    description: "A luminous Irish landscape at dusk — bare trees silhouetted against a blazing sunset, a quiet country road drawing the eye to the horizon. Oil on canvas."
  },
  {
    id: 7,
    title: "The Island from Above",
    artist: "Michael Gemmell",
    year: 2014,
    size: "80 × 80 cm",
    price: "€2,500",
    image: "https://customer-assets.emergentagent.com/job_deco-treasures/artifacts/vbb79rz6_Screenshot%202021-06-11%20at%2014.18.29.png",
    imageContext: "/gemmell-island-context-0.png",
    category: "Painting",
    featured: true,
    description: "A contemporary abstract evocation of an island viewed from above — sweeping greys, blacks and bone-white forms cut by quiet flashes of crimson and ember. Oil on canvas."
  }
];

export const antiqueItems = [
  {
    id: 6,
    title: "L'Infirmière et le Soldat Blessé",
    period: "c. 1915–1920",
    price: "€900",
    image: "/spelter-nurse-catalogue-0.png",
    imageContext: "/spelter-nurse-context-0.png",
    description: "French WWI patriotic spelter group — a Red Cross nurse hailing for help beside a wounded soldier in his Adrian helmet. Mounted on a turned red marble socle. Signed 'H. Trémo…' to the base; original orange enamel highlight surviving on the Red Cross armband. 60 × 30 cm.",
    origin: "France",
    kind: "acquisition"
  },
  {
    id: 8,
    title: "Art Deco French Liqueur Set",
    period: "1930s",
    price: "€350",
    image: "/arques-liqueur-catalogue-0.png",
    imageContext: "/arques-liqueur-context-0.png",
    description: "Elegant French Art Deco liqueur set, produced by the historic Verrerie et Cristallerie d'Arques (under G. Durand & Cie). Pink 'rosaline' pressed glass — a decanter with stopper, an octagonal tray with stepped geometric chevron motif, and six matching liqueur glasses. Complete and in fine condition. A characteristic example of 1930s French decorative glassmaking from the golden age of European Art Deco.",
    origin: "France"
  },
  {
    id: 9,
    title: "Patek Philippe Limoges Bonbonnière",
    period: "Limited Edition 2000",
    price: "€650",
    image: "/patek-bonbonniere-catalogue-0.png",
    imageContext: "/patek-bonbonniere-context-0.png",
    description: "A fine and unusual hand-decorated Limoges porcelain bonbonnière produced as a Patek Philippe Collection 2000 limited edition piece, reproducing the cobalt-and-gold enamelled case of an iconic 1928 Patek Philippe pocket watch (18-carat yellow gold, blue/gold enamelled back, urn-and-roses motif) held in the Patek Philippe Museum, Geneva. Octagonal porcelain de Limoges, signed 'Patek Philippe Genève'. Excellent condition with minimal wear. 10 × 4 × 3.5 cm.",
    origin: "France"
  },
  {
    id: 10,
    title: "Bay Keramik 'Fat Lava' Vase",
    period: "c. 1960s–70s",
    price: "€195",
    image: "/bay-fatlava-catalogue-0.png",
    imageContext: "/bay-fatlava-context-0.png",
    description: "Substantial West German 'Fat Lava' (Fettlava) vase by Bay-Keramik-Fabrik Bodo Mans of Ransbach-Baumbach. Cream stoneware base with a dripping amber-caramel glaze band and characteristic wavy scalloped flow line. Impressed marks to base: 'BAY • W-GERMANY • 240 40' (form 240, height 40 cm). A bold, large-format example of the West German Pottery movement that defined European mid-century ceramics. Excellent condition.",
    origin: "Germany"
  },
  {
    id: 11,
    title: "Petrol-Teal & Gilt Upcycled Miniature Armoire",
    period: "Upcycled vintage",
    price: "€120",
    image: "/jbox-catalogue-0.png",
    imageContext: "/jbox-context-0.png",
    description: "A charming miniature Florentine-style armoire / jewellery cabinet, lovingly upcycled in deep petrol-teal chalk paint with rubbed-back warm gold gilt edges and feet. Two arched glass doors with etched cherry-blossom and grass motifs, two small drawers below dressed in a delicate lace-pattern stencil, shaped scrolled bracket feet. A piece that bridges vintage romance and contemporary jewel-toned interiors. 30 × 20 × 13 cm.",
    origin: "France",
    kind: "acquisition"
  },
  {
    id: 7,
    title: "Schlumberger Alsace Coopered Champagne Bucket",
    period: "c. 1960",
    price: "€240",
    image: "/schlumberger-bucket-catalogue-0.png",
    imageContext: "/schlumberger-bucket-context-0.png",
    description: "French coopered oak champagne bucket from Domaines Schlumberger, Guebwiller, Alsace (maison founded 1810). Three copper bands and an embossed copper escutcheon bearing the Schlumberger coat of arms. Twin raised stave 'ear' handles in the traditional Alsatian cooper style. Warm honey patina, fine original condition. Height 33 cm including ears (28 cm to brim).",
    origin: "France"
  },
  {
    id: 1,
    title: "Vintage Silk Necktie Jar",
    period: "Mid-20th century",
    price: "€195",
    image: "/ties-cleanup-0.png",
    imageContext: "/ties-context-0.png",
    description: "A curated jar of vintage silk neckties — patterned, striped, paisley and floral. A wonderful gentleman's accessory or interior styling piece.",
    origin: "Ireland"
  }
];

export const locations = [
  {
    country: "France",
    city: "Montolieu",
    address: "167 Chemin De Franc 11170",
    phone: "+33 6 60 58 91 50",
    hours: "Tue-Sat: 10:00-19:00, Sun: 12:00-18:00"
  },
  {
    country: "Ireland",
    city: "Mullingar",
    address: "30-32 Oliver Plunket Street",
    phone: "+353 87 803 83 57",
    hours: "Tue-Sat: 11:00-18:00, Sun: 13:00-17:00"
  }
];

export const testimonials = [
  {
    id: 1,
    name: "Catherine Beaumont",
    role: "Private Collector",
    text: "Dave has a quiet, unerring eye. The gallery's contemporary roster is genuinely exciting — Irish painters, European sculptors, voices you don't find on every other wall. Each piece I've acquired has come with proper conversation and proper care.",
    rating: 5
  },
  {
    id: 2,
    name: "Michael O'Connor",
    role: "Interior Designer",
    text: "The Annex is the secret weapon. Napoleon III mantle pieces one week, a signed Val Saint Lambert the next — properly sourced, properly priced, and always something that actually fits a room. I've furnished half a dozen clients out of Chimera now.",
    rating: 5
  },
  {
    id: 3,
    name: "Isabelle Marchand",
    role: "Visiting Curator, Paris",
    text: "A gallery with a curator's instinct and a dealer's discipline — rare. The breadth from contemporary canvas to nineteenth-century French decorative is handled with real coherence. The kind of place you leave thinking about for days.",
    rating: 5
  }
];


// ──────────────────────────────────────────────────────────────
//  Artists
// ──────────────────────────────────────────────────────────────
//  Each artist's `slug` must match slugify(artist name) used in galleryItems.
//  `photo` can be null — the UI will fall back to the artist's representative
//  artwork or an elegant monogram placeholder.
// ──────────────────────────────────────────────────────────────

export const artists = [
  {
    slug: "mark-bailey",
    name: "Mark Bailey",
    nationality: "American",
    born: null,
    based: null,
    photo: null,
    shortBio: "Contemporary painter capturing working figures and quiet labour through loose painterly mark-making and abstract scaffolding.",
    bio: "Mark Bailey is a contemporary painter drawn to the dignity of work — chefs, makers, craftsmen — rendered in confident loose brushwork. His paintings let abstract structure and observed figure exist on the same surface, asking the viewer to assemble the scene."
  },
  {
    slug: "kyle-barnes",
    name: "Kyle Barnes",
    nationality: "Irish",
    born: null,
    based: null,
    photo: null,
    shortBio: "Contemporary painter working in hyper-real oil — exploring the human figure, sculpture and surface through bold colour and uncompromising scale.",
    bio: "Kyle Barnes is a contemporary painter whose hyper-real oils interrogate the body, the bust and the painted surface itself. His large-scale works treat the human head as both subject and object — patinated, dripped, recoloured — set against unapologetically saturated grounds."
  },
  {
    slug: "igor-melnyk",
    name: "Igor Melnyk",
    nationality: "Ukrainian",
    born: null,
    based: null,
    photo: null,
    shortBio: "Contemporary painter working in vigorous impasto, exploring landscape and atmosphere through gestural mark-making.",
    bio: "Igor Melnyk is a contemporary painter whose oils on board carry the weight and tactility of heavy impasto. Drawing on the Eastern European painterly tradition, his landscapes capture the colour and feeling of places in transition — seasons turning, light fading, summer giving way to autumn."
  },
  {
    slug: "lesley-oldaker",
    name: "Lesley Oldaker",
    nationality: null,
    born: null,
    based: null,
    photo: null,
    shortBio: "Contemporary painter of crowds, corridors and quiet dissent — her figures elongated and trailing, set inside vast architectural perspectives.",
    bio: "Lesley Oldaker is a contemporary painter whose oils explore the geometry of the crowd. Working in cool palettes of cobalt, slate and white, she stages anonymous figures within sweeping architectural spaces — tunnels, concourses, vaulted halls — where a single coloured form, often a solitary red, becomes the emotional anchor. Her canvases hold both stillness and a low cinematic tension."
  },
  {
    slug: "brian-smyth",
    name: "Brian Smyth",
    nationality: "Irish",
    born: null,
    based: null,
    photo: null,
    shortBio: "Contemporary painter of urban scenes, capturing the quiet drama of public spaces and human figures within architectural settings.",
    bio: "Brian Smyth is a contemporary painter whose work observes the rhythms of urban life. Drawn to piazzas, plazas and squares, he paints from elevated viewpoints that reveal the choreography of people moving through historic spaces."
  },
  {
    slug: "kate-beagan",
    name: "Kate Beagan",
    nationality: "Irish",
    born: null,
    based: null,
    photo: null,
    shortBio: "Contemporary Irish landscape painter known for luminous, atmospheric depictions of the Irish countryside at dusk and dawn.",
    bio: "Kate Beagan is a contemporary Irish painter whose work captures the fleeting light of the Irish landscape. Working primarily in oils, she is celebrated for her evocative renderings of rural roads, hedgerows and skies at the cusp of day and night."
  },
  {
    slug: "michael-gemmell",
    name: "Michael Gemmell",
    nationality: "Irish",
    born: null,
    based: null,
    photo: null,
    shortBio: "Contemporary abstract painter exploring landscape, memory and form through bold gestural compositions.",
    bio: "Michael Gemmell is a contemporary painter whose abstract compositions reduce landscape and memory to their essential forms. His work is characterised by confident gestural marks, restrained palettes and quiet emotional weight."
  },
  {
    slug: "dirk-hudson",
    name: "Dirk Hudson",
    nationality: null,
    born: null,
    based: null,
    photo: null,
    shortBio: "Contemporary sculptor working in steel, copper and beeswax — monolithic figures touched by veins of molten bronze.",
    bio: "Dirk Hudson is a contemporary sculptor whose figural works fuse forged blackened steel with patinated copper and beeswax. His pieces stand as quiet monuments — hooded figures and ancestral forms in which the cold weight of the metal is broken open by warm copper veins, faces and roots. The result is a body of work that feels both ancient and intimate."
  },
  {
    slug: "eamonn-higgins",
    name: "Eamonn Higgins",
    nationality: null,
    born: null,
    based: null,
    photo: null,
    shortBio: "Contemporary sculptor of the human figure — portrait busts in bronze that hold memory, time and quiet emotional weight.",
    bio: "Eamonn Higgins is a contemporary sculptor whose practice centres on the human face. Working primarily in bronze, mounted on polished stone, his portrait busts are studies in patience and time — every line, every weathering of the skin carrying its own narrative. His work refuses idealisation in favour of honest, attentive presence."
  }
];

// Helper: get all gallery works by a given artist slug
export const getWorksByArtist = (artistSlug) =>
  galleryItems.filter((work) => slugify(work.artist) === artistSlug);

// Helper: find artist by slug
export const getArtistBySlug = (artistSlug) =>
  artists.find((a) => a.slug === artistSlug);
